import { FC, InputHTMLAttributes, useState, useRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@new/lib';

export interface PasswordInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
}

export const PasswordInput: FC<PasswordInputProps> = ({ value, onChange, hasError, ...rest }) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const type = showPassword ? 'text' : 'password';
  const Icon = showPassword ? Eye : EyeOff;

  const toggleVisibility = () => {
    setShowPassword((prev) => !prev);
    const input = inputRef.current;

    if (input) {
      requestAnimationFrame(() => {
        input.focus();
        const length = input.value.length;
        input.setSelectionRange(length, length);
      });
    }
  };

  return (
    <div className="relative w-full focus-within:[&>button>svg]:stroke-(--neutral-black-NB1000)!">
      <input
        {...rest}
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-slot="input"
        className={cn(
          'peer! l3! bg-(--neutral-white-NW800)! regular',
          'w-full h-11 md:h-12.5  xl:h-16.5 p-4! rounded-md! border-2!',
          '[&&]:[font-family:var(--font-montserrat)]! [[&&]:[font-size:var(--fs-l4)]! &&]:lg:[font-size:var(--fs-l3)]! [&&]:leading(--lh-b)! [&&]:text-(--neutral-black-NB1000)!',
          '[&&]:placeholder:[font-family:var(--font-montserrat)]! [&&]:placeholder:[font-size:var(--fs-l4)]! [&&]:lg:placeholder:[font-size:var(--fs-l3)]! [&&]:placeholder:leading(--lh-b)! [&&]:placeholder:text-(--neutral-white-NW600)!',
          'focus:border-(--neutral-black-NB1000)/80! focus:outline-none! focus:border-2!',
          hasError ? ' border-(--danger-D700)/80!' : 'border-transparent/80!'
        )}
      />
      <button
        type="button"
        className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
        onClick={toggleVisibility}
      >
        <Icon
          className={cn('w-5 lg:w-6 h-5 lg:h-6', 'stroke-(--neutral-white-NW500)!')}
          strokeWidth={1.5}
          size={16}
        />
      </button>
    </div>
  );
};
